import { useEffect } from "react";
import { Monitor, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { BrandConfig } from "@/contexts/BrandContext";
import { BrandLandingPresentation } from "@/components/brand/BrandLandingPresentation";
import { getSupportedBrandFont, isGoogleBrandFont } from "@/lib/brand/fontCatalog";
import { cn } from "@/lib/utils";

interface BrandStudioPreviewProps {
  config: BrandConfig;
  viewport: "desktop" | "mobile";
  onViewportChange: (viewport: "desktop" | "mobile") => void;
  title?: string;
}

export function BrandStudioPreview({ config, viewport, onViewportChange, title = "Live preview" }: BrandStudioPreviewProps) {
  useEffect(() => {
    const fonts = [config.font_config?.heading, config.font_config?.body]
      .map(getSupportedBrandFont)
      .filter((font): font is string => Boolean(font) && isGoogleBrandFont(font));
    const uniqueFonts = Array.from(new Set(fonts));
    document.getElementById("brand-studio-google-fonts")?.remove();
    if (!uniqueFonts.length) return;
    const link = document.createElement("link");
    link.id = "brand-studio-google-fonts";
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${uniqueFonts.map((font) => `family=${encodeURIComponent(font)}:wght@400;500;600;700`).join("&")}&display=swap`;
    document.head.appendChild(link);
    return () => link.remove();
  }, [config.font_config?.heading, config.font_config?.body]);

  return (
    <aside className="brand-studio-preview lg:sticky lg:top-6 lg:self-start">
      <div className="brand-studio-preview-bar flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-xs text-muted-foreground">Updates as you type</p>
        </div>
        <div className="flex items-center rounded-md border bg-component-background p-1" aria-label="Preview size">
          <Button type="button" size="icon" variant={viewport === "desktop" ? "secondary" : "ghost"} className="h-7 w-7" onClick={() => onViewportChange("desktop")} aria-label="Desktop preview">
            <Monitor className="h-4 w-4" />
          </Button>
          <Button type="button" size="icon" variant={viewport === "mobile" ? "secondary" : "ghost"} className="h-7 w-7" onClick={() => onViewportChange("mobile")} aria-label="Mobile preview">
            <Smartphone className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="brand-studio-canvas">
        <div className={cn("brand-studio-page", viewport === "mobile" && "brand-studio-page-mobile")}>
          <BrandLandingPresentation config={config} fallbackName="Your brand" preview mobile={viewport === "mobile"} />
        </div>
      </div>
    </aside>
  );
}